import { Fragment, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { thumbnailUrl } from "@/utils/imageUrl";
import { LOOKS } from "./portfolioLooks";

const OCCASIONS = [
    "date",
    "client meeting",
    "day to day",
    "dinner night out",
];

interface JoySectionBassProps {
    /** Show only looks styled from a given closet (e.g. 'his' for the men's page). */
    closetFilter?: "his" | "her";
    /**
     * Ordering of the carousel. `'mens-first'` puts all `closet === 'his'`
     * cards up front so the default carousel view shows only men's looks;
     * women's looks are revealed as the user scrolls horizontally. Used on
     * the splash where we want men-first with womens still discoverable.
     * `closetFilter` still takes precedence — pass one or the other, not both.
     */
    order?: "default" | "mens-first";
    /** Splash pages: below md, render the mobile snap rail instead of the desktop carousel. */
    mobileRedesign?: boolean;
}

const JoySectionBass = ({ closetFilter, order = "default", mobileRedesign = false }: JoySectionBassProps = {}) => {
    const looks = closetFilter
        ? LOOKS.filter((l) => l.closet === closetFilter)
        : order === "mens-first"
            ? [...LOOKS].sort((a, b) => {
                if (a.closet === b.closet) return 0;
                return a.closet === "his" ? -1 : 1;
            })
            : LOOKS;
    const [occasionIndex, setOccasionIndex] = useState(0);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const mobileRailRef = useRef<HTMLDivElement>(null);
    const reduceMotion = useReducedMotion();

    useEffect(() => {
        if (reduceMotion) return;
        const interval = setInterval(() => {
            setOccasionIndex((i) => (i + 1) % OCCASIONS.length);
        }, 1600);
        return () => clearInterval(interval);
    }, [reduceMotion]);

    // Mobile rail: one card (260px) plus its gap per tap.
    const scrollMobileRail = (dir: number) => {
        mobileRailRef.current?.scrollBy({ left: dir * 276, behavior: "smooth" });
    };

    // Page the carousel by ~85% of the visible width (user-driven; no auto-scroll).
    const scrollByPage = (dir: number) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
    };

    // Mobile: heading + arrow controls, then a snap rail of every look (same
    // order/filter as desktop). Cards carry the closet caption and item thumbs.
    const mobile = (
        <section className="md:hidden bg-[#FFF7F0] pt-2 pb-20">
            <div className="flex items-end justify-between gap-4 px-6">
                <div>
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8B11]">The Portfolio</p>
                    <h2 className="font-playfair text-[34px] font-bold leading-[1.1] text-[#0f172a]">
                        Look your best for your
                        <br />
                        <span className="inline-block min-h-[42px] text-[#c2410c]">{OCCASIONS[occasionIndex]}</span>
                    </h2>
                </div>
                <div className="flex flex-none gap-2">
                    <button
                        type="button"
                        onClick={() => scrollMobileRail(-1)}
                        aria-label="Previous looks"
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e7e5e4] bg-white text-[#0f172a] shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollMobileRail(1)}
                        aria-label="Next looks"
                        className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0f172a] text-white shadow-[0_4px_12px_rgba(15,23,42,0.2)]"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
            </div>
            <div
                ref={mobileRailRef}
                className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 pt-7 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                {looks.map((look, index) => (
                    <div
                        key={index}
                        className="w-[260px] flex-none snap-start overflow-hidden rounded-2xl bg-white shadow-[0_20px_40px_-24px_rgba(15,23,42,0.35)]"
                    >
                        <div className="aspect-[2/3] w-full bg-[#f3efe9]">
                            {look.hero && (
                                <img
                                    src={thumbnailUrl(look.hero, { width: 520 })}
                                    alt={`Outfit styled from ${look.closet} closet`}
                                    className="h-full w-full object-cover object-top"
                                />
                            )}
                        </div>
                        <div className="px-4 pb-4 pt-3.5">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#0f172a]">
                                    Styled from {look.closet} closet
                                </span>
                                <span className="whitespace-nowrap font-playfair text-[13px] italic text-[#c2410c]">
                                    {look.items.length} piece{look.items.length === 1 ? "" : "s"}
                                </span>
                            </div>
                            {look.items.length > 0 && (
                                <div className="mt-3 flex items-center gap-1.5">
                                    {look.items.map((item, i) => (
                                        <Fragment key={i}>
                                            {i > 0 && (
                                                <span className="shrink-0 font-playfair text-[#c9bfb2]" aria-hidden="true">+</span>
                                            )}
                                            <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-[#f3efe9]">
                                                {item && (
                                                    <img
                                                        src={thumbnailUrl(item, { width: 160 })}
                                                        alt=""
                                                        aria-hidden="true"
                                                        className="h-full w-full object-cover object-center"
                                                    />
                                                )}
                                            </div>
                                        </Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );

    const desktop = (
        <section className="relative bg-[#FFF7F0] overflow-hidden py-16 sm:py-20 border-y border-[#e2d8ce]">
            {/* Minimalist Architectural Background */}
            <div className="absolute inset-0 bg-[#FFF7F0]" />
            <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-[#c2410c]/5 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-[#c2410c]/5 blur-[120px] rounded-full" />
            <div className="absolute inset-0 bg-[radial-gradient(#e2d8ce_1px,transparent_1px)] [background-size:24px_24px] opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#FFF7F0]/60 via-transparent to-[#FFF7F0]/60" />

            <div className="max-w-[1400px] mx-auto relative z-10 px-4 sm:px-8 md:px-16 flex flex-col items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex flex-col items-center"
                >
                    <span className="text-[#0f172a] font-bold tracking-[0.2em] uppercase mb-4 text-[10px] sm:text-[11px]">The Portfolio</span>
                    <h2 className="text-center whitespace-nowrap text-[18px] sm:text-[28px] md:text-[40px] lg:text-[48px] font-bold font-playfair leading-[1.05] tracking-tight text-[#0f172a] mb-10">
                        Look your best for your{" "}
                        <span className="relative inline-grid overflow-hidden align-baseline pb-[0.18em] -mb-[0.18em] pt-[0.08em] -mt-[0.08em]">
                            {/* invisible sizer: reserves the width of the widest word so
                                the static text never shifts as the word rotates */}
                            <span className="col-start-1 row-start-1 invisible whitespace-nowrap" aria-hidden="true">
                                dinner night out
                            </span>
                            <AnimatePresence mode="sync">
                                <motion.span
                                    key={OCCASIONS[occasionIndex]}
                                    initial={{ y: "-100%" }}
                                    animate={{ y: "0%" }}
                                    exit={{ y: "100%" }}
                                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                                    className="col-start-1 row-start-1 text-left text-[#c2410c] whitespace-nowrap"
                                >
                                    {OCCASIONS[occasionIndex]}
                                </motion.span>
                            </AnimatePresence>
                        </span>
                    </h2>
                </motion.div>

                {/* User-driven carousel: swipe / trackpad scroll on any device, plus
                    prev/next arrows on md+. One calm row, no auto-scroll. */}
                <div className="relative w-full">
                    <button
                        type="button"
                        onClick={() => scrollByPage(-1)}
                        aria-label="Previous looks"
                        className="hidden md:flex absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-white/95 border border-[#e7ded3] shadow-[0_6px_20px_rgba(0,0,0,0.12)] text-[#0a0502] transition hover:bg-white hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2410c] focus-visible:ring-offset-2"
                    >
                        <ChevronLeft className="h-5 w-5" strokeWidth={2} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollByPage(1)}
                        aria-label="Next looks"
                        className="hidden md:flex absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-white/95 border border-[#e7ded3] shadow-[0_6px_20px_rgba(0,0,0,0.12)] text-[#0a0502] transition hover:bg-white hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2410c] focus-visible:ring-offset-2"
                    >
                        <ChevronRight className="h-5 w-5" strokeWidth={2} />
                    </button>

                    <motion.div
                        ref={scrollerRef}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        role="region"
                        aria-label="Styled looks and the items they were built from"
                        tabIndex={0}
                        className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {looks.map((look, index) => (
                            <div
                                key={index}
                                className="snap-start shrink-0 w-[64%] sm:w-[40%] md:w-[30%] lg:w-[calc((100%_-_5rem)/5_-_0.7rem)] bg-white p-2.5 sm:p-3 rounded-[3px] shadow-[0_10px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_44px_rgba(0,0,0,0.12)] hover:-translate-y-1.5 border border-[#e7ded3] transition duration-300 flex flex-col"
                            >
                                {/* Hero look — full image HEIGHT always shown (head-to-toe);
                                    extra width is cropped, centered. */}
                                <div className="w-full aspect-[2/3] relative overflow-hidden bg-[#f3efe9] rounded-[2px]">
                                    {look.hero && (
                                        <img
                                            src={thumbnailUrl(look.hero, { width: 640 })}
                                            alt={`Outfit styled from ${look.closet} closet`}
                                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto max-w-none"
                                        />
                                    )}
                                </div>

                                {/* Caption: closet + piece count, split by a hairline rule */}
                                <div className="mt-4 pt-3 border-t border-[#e7ded3] flex items-start justify-between gap-2.5">
                                    <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.16em] text-[#0a0502] leading-[1.4]">
                                        Styled from {look.closet} closet
                                    </span>
                                    <span className="font-playfair italic text-[12px] sm:text-[13px] text-[#c2410c] whitespace-nowrap leading-none mt-[1px]">
                                        {look.items.length} piece{look.items.length === 1 ? "" : "s"}
                                    </span>
                                </div>

                                {/* The individual garments that make up the look */}
                                {look.items.length > 0 && (
                                    <div className="mt-3.5 flex items-center gap-x-1.5">
                                        {look.items.map((item, i) => (
                                            <Fragment key={i}>
                                                <div className="flex-1 aspect-square rounded-[2px] overflow-hidden bg-[#f3efe9] border border-[#e7ded3]">
                                                    {item && (
                                                        <img
                                                            src={thumbnailUrl(item, { width: 200 })}
                                                            alt=""
                                                            aria-hidden="true"
                                                            className="w-full h-full object-cover object-center"
                                                        />
                                                    )}
                                                </div>
                                                {i < look.items.length - 1 && (
                                                    <span className="shrink-0 text-[#c9bfb2] text-[11px] font-playfair select-none" aria-hidden="true">+</span>
                                                )}
                                            </Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );

    if (!mobileRedesign) return desktop;

    return (
        <>
            {mobile}
            <div className="hidden md:block">{desktop}</div>
        </>
    );
};

export default JoySectionBass;
